/**
 * LibraryMemberSearch — type-ahead user search for adding members to a library.
 * Uses the same /v1/users/search endpoint as the Board ShareModal.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../../services/api-client';
import * as pagesApi from '../../api/pages-api';
import type { LibraryMember, LibraryMemberRole } from '../../api/pages-api';

interface UserSearchResult {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
}

interface SearchResponse {
  data: UserSearchResult[];
}

interface LibraryMemberSearchProps {
  readonly libraryId: string;
  readonly existingMemberIds: string[];
  readonly defaultRole: LibraryMemberRole;
  readonly onMemberAdded: (member: LibraryMember) => void;
}

export function LibraryMemberSearch({
  libraryId,
  existingMemberIds,
  defaultRole,
  onMemberAdded,
}: LibraryMemberSearchProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [adding, setAdding] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter out existing members
  const filteredResults = results.filter((r) => !existingMemberIds.includes(r.userId));

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await api.get<SearchResponse>('/v1/users/search', { q: query });
        setResults(response.data);
        setIsOpen(true);
        setActiveIndex(-1);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const handleSelect = useCallback(async (user: UserSearchResult) => {
    setAdding(user.userId);
    try {
      const member = await pagesApi.addMember(libraryId, { userId: user.userId, role: defaultRole });
      onMemberAdded(member);
      setQuery('');
      setResults([]);
      setIsOpen(false);
    } catch {
      // Add failed
    } finally {
      setAdding(null);
    }
  }, [libraryId, defaultRole, onMemberAdded]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || filteredResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < filteredResults.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : filteredResults.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < filteredResults.length) {
        handleSelect(filteredResults[activeIndex]!);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, [isOpen, filteredResults, activeIndex, handleSelect]);

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (filteredResults.length > 0) setIsOpen(true); }}
        placeholder="Search by name or email…"
        className="w-full px-2 py-1 text-xs border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
        aria-label="Search users to add"
        aria-expanded={isOpen}
        aria-controls="library-member-search-results"
        aria-activedescendant={activeIndex >= 0 ? `lib-search-${activeIndex}` : undefined}
      />
      {isLoading && <span className="absolute right-2 top-1.5 text-xs text-muted-foreground">…</span>}

      {isOpen && filteredResults.length > 0 && (
        <ul
          id="library-member-search-results"
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-40 overflow-y-auto"
        >
          {filteredResults.map((user, index) => (
            <li
              key={user.userId}
              id={`lib-search-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => handleSelect(user)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`px-2 py-1.5 text-xs cursor-pointer ${
                index === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              } ${adding === user.userId ? 'opacity-50' : ''}`}
            >
              <span className="font-medium">{user.displayName}</span>
              <span className="text-muted-foreground ml-1">{user.email}</span>
            </li>
          ))}
        </ul>
      )}

      {isOpen && query.length >= 2 && !isLoading && filteredResults.length === 0 && (
        <p className="absolute z-50 w-full mt-1 px-2 py-1.5 text-xs text-muted-foreground bg-popover border rounded-md shadow-md">
          No users found.
        </p>
      )}
    </div>
  );
}
